/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

export default $ => css`
  .user-nav-dropdown {
    .sb-avatar {
      transition: transform 150ms ease-in-out, outline 150ms ease-in-out;
      &:hover {
        transform: scale(1.1);
        outline: 2px solid ${$.primaryMain};
        outline-offset: 2px;
      }
    }
  }

  .user-dropdown-menu {
    background: white;
    min-width: 280px;
    border: 1px solid ${$.gray300};
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .user-dropdown-item {
    display: flex;
    text-align: center;
    align-items: center;
    padding-top: 0.7rem !important;
    padding-bottom: 0.7rem !important;
    font-size: 0.92rem;
    color: inherit;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: ${$.primaryMain}12;
      color: ${$.primaryMain};

      .fa,
      .fas {
        color: ${$.primaryMain} !important;
      }
    }

    &.text-danger {
      &:hover,
      &:focus {
        background-color: #f443361a;
        color: #f44336 !important;

        .fa,
        .fas {
          color: #f44336 !important;
        }
      }
    }
  }

  /* ── Notification bell + count badge ───────────────────────────── */
  .notification-bell-toggle {
    display: inline-flex;
    align-items: center;
  }

  /* Ghost button: bell + (optional) "N new notifications" label, shown on non-mobile navbars */
  .notification-bell-toggle--expanded {
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
    transition: background-color 150ms ease;

    &:hover {
      background-color: ${$.primaryMain}0d;
    }
  }

  .notification-bell-icon-wrap {
    position: relative;
    display: inline-flex;
  }

  .notification-bell-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background-color: ${$.dangerMain};
    box-shadow: 0 0 0 2px ${$.white};
    pointer-events: none;
  }

  /* Grid-track trick: animates from zero to its natural content width so the
     button smoothly grows when a label appears, instead of popping in. */
  .notification-bell-label-wrap {
    display: grid;
    grid-template-columns: 0fr;
    min-width: 0;
    margin-left: 0;
    overflow: hidden;
    transition: grid-template-columns 380ms cubic-bezier(0.16, 1, 0.3, 1), margin-left 380ms cubic-bezier(0.16, 1, 0.3, 1);

    &.has-unread {
      grid-template-columns: 1fr;
      margin-left: 0.5rem;

      .notification-bell-label {
        opacity: 1;
      }
    }
  }

  .notification-bell-label {
    display: block;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${$.primaryMain};
    opacity: 0;
    transition: opacity 260ms ease 60ms;
  }

  .notification-count-badge {
    position: absolute;
    top: -4px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background-color: ${$.dangerMain};
    color: ${$.white};
    font-size: 0.62rem;
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 0 0 2px ${$.white};
    pointer-events: none;
  }

  /* ── Notification dropdown ─────────────────────────────────────── */
  .notification-dropdown-menu {
    width: 460px;
    max-height: 620px;
    border: 1px solid ${$.gray300};
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);

    /* Only apply flex when Bootstrap adds .show — avoids overriding display:none */
    &.show {
      display: flex;
      flex-direction: column;
    }
  }

  .notification-panel-header {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    flex-wrap: wrap;
    background: ${$.white};
  }

  .notification-header-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1.3rem;
    height: 1.3rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background-color: ${$.dangerMain};
    color: ${$.white};
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1;
  }

  .notification-list {
    overflow-y: auto;
    flex: 1;
  }

  .notification-item {
    position: relative;
    padding: 0.85rem 1.1rem 0.85rem 1.4rem;
    cursor: pointer;
    border-bottom: 1px solid ${$.gray200};
    transition: background-color 120ms ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${$.primaryMain}0d;
    }

    &.unread {
      background-color: ${$.primaryMain}08;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 3px;
        background-color: ${$.primaryMain};
      }
    }
  }

  .notification-preview-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  .notification-unread-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${$.dangerMain};
    box-shadow: 0 0 0 2px ${$.dangerMain}40;
    margin-top: 4px;
    flex-shrink: 0;
    cursor: pointer;
    transition: transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease;

    &:hover {
      transform: scale(1.35);
      background-color: ${$.dangerMain}cc;
      box-shadow: 0 0 0 3px ${$.dangerMain}30;
    }

    &.cleared {
      background-color: ${$.gray400};
      box-shadow: none;
      cursor: default;
      pointer-events: none;

      &:hover {
        transform: none;
      }
    }
  }

  .notification-link-btn {
    font-size: 0.75rem;
    line-height: 1;
    color: ${$.primaryMain} !important;
    opacity: 0.75;
    transition: opacity 120ms ease, transform 120ms ease;

    &:hover {
      opacity: 1;
      transform: translateX(1px);
    }
  }

  .notification-full-content {
    p {
      margin-bottom: 0.25rem;
    }
  }
`;
